/**
 * ModelParametersPanel Component
 *
 * Standalone panel for model parameters (temperature, top_p, max_tokens, etc.)
 * Used when you need to show parameters separately from model selection.
 */

import React from 'react';
import { LinkedParameter } from './types';
import { useI18n } from '../../i18n';

interface ModelParametersPanelProps {
  parameters: LinkedParameter[];
  onParameterChange: (name: string, value: any) => void;
  compact?: boolean;
}

/**
 * Button group for select-type parameters
 */
function ButtonGroup({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`
            px-2 py-0.5 text-xs rounded-md font-medium transition-all
            ${value === opt.value
              ? 'bg-blue-500 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
          `}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Single parameter control
 */
function ParameterControl({
  param,
  onChange,
  compact = false,
}: {
  param: LinkedParameter;
  onChange: (value: any) => void;
  compact?: boolean;
}) {
  const { language } = useI18n();
  const labelWidth = compact ? 'w-16' : 'w-20';

  if (param.type === 'button_group' && param.options) {
    return (
      <div className="flex items-center gap-2">
        <label className={`${labelWidth} text-xs text-gray-500 flex-shrink-0`}>
          {param.label}
        </label>
        <ButtonGroup
          options={param.options}
          value={param.value ?? ''}
          onChange={onChange}
        />
      </div>
    );
  }

  if (param.type === 'select' && param.options) {
    return (
      <div className="flex items-center gap-2">
        <label className={`${labelWidth} text-xs text-gray-500 flex-shrink-0`}>
          {param.label}
        </label>
        <select
          value={param.value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded bg-white"
        >
          {param.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (param.type === 'number') {
    const hasRange = param.min !== undefined && param.max !== undefined && param.max <= 2;

    if (hasRange) {
      return (
        <div className="flex items-center gap-2">
          <label className={`${labelWidth} text-xs text-gray-500 flex-shrink-0`}>
            {param.label}
          </label>
          <input
            type="range"
            min={param.min}
            max={param.max}
            step={param.step ?? 0.1}
            value={param.value ?? param.min ?? 0}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <input
            type="number"
            min={param.min}
            max={param.max}
            step={param.step ?? 0.1}
            value={param.value ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : parseFloat(e.target.value);
              onChange(val);
            }}
            className="w-14 px-1.5 py-1 text-xs border border-gray-200 rounded text-center"
          />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <label className={`${labelWidth} text-xs text-gray-500 flex-shrink-0`}>
          {param.label}
        </label>
        <input
          type="number"
          min={param.min}
          max={param.max}
          step={param.step ?? 1}
          placeholder={language === 'zh' ? '默认' : 'Default'}
          value={param.value ?? ''}
          onChange={(e) => {
            const val = e.target.value === '' ? null : parseFloat(e.target.value);
            onChange(val);
          }}
          className="w-20 px-2 py-1 text-xs border border-gray-200 rounded"
        />
      </div>
    );
  }

  return null;
}

export const ModelParametersPanel: React.FC<ModelParametersPanelProps> = ({
  parameters,
  onParameterChange,
  compact = false,
}) => {
  if (parameters.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-x-6 gap-y-2 ${compact ? 'py-2' : 'py-3'}`}>
      {parameters.map((param) => (
        <ParameterControl
          key={param.name}
          param={param}
          onChange={(val) => onParameterChange(param.name, val)}
          compact={compact}
        />
      ))}
    </div>
  );
};

export default ModelParametersPanel;
